import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import Select from "react-select";
import Confetti from 'react-confetti';
import { useNavigate } from "react-router-dom";
import {
  DraftRoomV2Wrapper,
  DraftTopBar,
  TopBarRow,
  TimerBox,
  OnTheClockCard,
  PickStrip,
  PickTile,
  DraftRoomV2Grid,
  Panel,
  PanelHeader,
  PanelBody,
  PanelBodyScroll,
  ToggleRow,
  MiniTable,
  MainPanel,
  DraftStatus,
  StatusAvatar,
  StatusText,
  AutopickActionButton,
  TabRow,
  Tab,
  FilterBar,
  PillSelect,
  SearchInput,
  PlayerTableWrap,
  PlayerTable,
  DraftCompleteMessage,
  PicksList,
  PickCard,
  DraftCompleteModalOverlay,
  DraftCompleteModalCard,
  DraftCompleteModalHeader,
  DraftCompleteModalClose,
  DraftCompleteModalBody,
  DraftCompleteModalFooter,
  DraftCompleteModalCopyButton,
  RightPanelTabRow,
  RightPanelTab,
  TradeOfferCard,
  TradeGroupLabel,
  TradesEmpty,
} from "./DraftRoomComponentV2.styles";
import {
  getDraftRoomV2Players,
  selectDraftRoomV2Players,
} from "../../app/features/draftRoomV2Players/draftRoomV2PlayersSlice";
import { selectUser } from "../../app/features/user/userSlice";
import nflLogo from "../../assets/img/logo.png";
import { POSITIONS_COLOR } from "../../utils/constants";
import axiosInstance from "../../service/axiosInstance";
import TokenService from "../../service/token.service";
import { API_ENDPOINT, SOCKET_URL } from "../../config/config";
import { toast } from "react-toastify";
import { HOME } from "../../router/route-path";
import ProposeTradeModal from "./ProposeTradeModal";

const getPriorityValue = (priority) => {
  if (!priority) return Number.MAX_SAFE_INTEGER;
  const numeric = Number(String(priority).replace(/[^0-9]/g, ""));
  return Number.isFinite(numeric) ? numeric : Number.MAX_SAFE_INTEGER;
};

const getUtcTimestamp = (value) => {
  if (!value) return null;
  const hasTimezone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value);
  const normalized = hasTimezone ? value : `${value}Z`;
  const timestamp = Date.parse(normalized);
  return Number.isNaN(timestamp) ? null : timestamp;
};

const toNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const toBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true" || normalized === "1") return true;
    if (normalized === "false" || normalized === "0") return false;
  }
  return null;
};

const normalizeQueuePlayerIds = (playerIds) => {
  if (!Array.isArray(playerIds)) return [];
  const normalized = playerIds
    .map((playerId) => toNumber(playerId))
    .filter((playerId) => playerId != null);
  return Array.from(new Set(normalized));
};

const areNumberArraysEqual = (left, right) => {
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) {
      return false;
    }
  }
  return true;
};

const getPickIndexValue = (pick) =>
  toNumber(pick?.overall_index ?? pick?.index ?? pick?.pick_index ?? pick?.id);

const getPickRoundNumber = (pick) =>
  toNumber(pick?.round_index_number ?? pick?.round_number ?? pick?.round);

const getPickInRoundValue = (pick) =>
  toNumber(pick?.pick ?? pick?.pick_in_round ?? pick?.pick_number);

const getPickTeamId = (pick) =>
  pick?.team_id ?? pick?.owner_team_id ?? pick?.team?.id ?? pick?.round?.id ?? null;

const getPickTeamName = (pick) =>
  pick?.team_name ?? pick?.owner_team_name ?? pick?.team?.name ?? pick?.round?.name ?? null;

const normalizePickPayload = (payload) =>
  payload?.pick ?? payload?.data ?? payload?.payload ?? payload;

const getCurrentAccessToken = () => {
  try {
    return TokenService.getUser()?.tokens?.access ?? null;
  } catch (error) {
    return null;
  }
};

const getDraftWebSocketUrl = (eventId, accessToken) => {
  const numericEventId = toNumber(eventId);
  if (numericEventId == null || !accessToken) return null;
  const baseUrlValue =
    process.env.REACT_APP_DRAFT_WS_BASE_URL
    || SOCKET_URL;
  if (!baseUrlValue) return null;
  try {
    const normalizedBase = baseUrlValue.endsWith("/")
      ? baseUrlValue
      : `${baseUrlValue}/`;
    const wsUrl = new URL(normalizedBase);
    if (wsUrl.protocol === "http:") {
      wsUrl.protocol = "ws:";
    } else if (wsUrl.protocol === "https:") {
      wsUrl.protocol = "wss:";
    }
    wsUrl.pathname = `/ws/draft/${numericEventId}/`;
    wsUrl.searchParams.set("token", accessToken);
    wsUrl.hash = "";
    return wsUrl.toString();
  } catch (error) {
    return null;
  }
};


function DraftRoomComponentV2({ eventId }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { results: playerResults, loading: isPlayersLoading } =
    useSelector(selectDraftRoomV2Players);
  const { userInfo } = useSelector(selectUser);
  const [playerSearchQuery, setPlayerSearchQuery] = useState("");
  const [selectedPositions, setSelectedPositions] = useState(["all"]);
  const [totalRounds, setTotalRounds] = useState(4);
  const [teamList, setTeamList] = useState([]);
  const [simulatorPicks, setSimulatorPicks] = useState([]);
  const [eventPicks, setEventPicks] = useState([]);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [picksLoading, setPicksLoading] = useState(false);
  const [queuedPlayerIds, setQueuedPlayerIds] = useState([]);
  const [autoEnabled, setAutoEnabled] = useState(true);
  const [autoPickByTeamId, setAutoPickByTeamId] = useState({});
  const [showDrafted, setShowDrafted] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(30);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [draftAccessToken, setDraftAccessToken] = useState(() => getCurrentAccessToken());
  const [liveStatus, setLiveStatus] = useState(null);
  const [liveCurrentPickIndex, setLiveCurrentPickIndex] = useState(null);
  const [livePickDeadlineAt, setLivePickDeadlineAt] = useState(null);
  const [liveSecondsRemaining, setLiveSecondsRemaining] = useState(0);
  const [hasSelectingStarted, setHasSelectingStarted] = useState(false);
  const [currentPickIndex, setCurrentPickIndex] = useState(0);
  const [drafted, setDrafted] = useState([]);
  const [isDraftComplete, setIsDraftComplete] = useState(false);
  const [isFinishedEventModalOpen, setIsFinishedEventModalOpen] = useState(false);
  const [isMakingPick, setIsMakingPick] = useState(false);
  const [isDraftCompleteModalOpen, setIsDraftCompleteModalOpen] = useState(false);
  const [selectedRosterTeamId, setSelectedRosterTeamId] = useState(null);
  const [isRosterTeamManual, setIsRosterTeamManual] = useState(false);
  const [activeTab, setActiveTab] = useState("players");
  const [isTradeModalOpen, setIsTradeModalOpen] = useState(false);
  const [tradesById, setTradesById] = useState(new Map());
  const [rightPanelTab, setRightPanelTab] = useState("picks");
  const timerRef = useRef(null);
  const pickListRef = useRef([]);
  const submitPickInFlightRef = useRef(false);
  const queuedPlayerIdsRef = useRef([]);
  const myTeamIdRef = useRef(null);
  const draftWsRef = useRef(null);
  const draftWsReconnectTimerRef = useRef(null);
  const draftWsReconnectAttemptRef = useRef(0);
  const shouldReconnectDraftWsRef = useRef(false);

  const sendDraftWsMessage = useCallback((message) => {
    const ws = draftWsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      return false;
    }
    try {
      ws.send(JSON.stringify(message));
      return true;
    } catch (error) {
      return false;
    }
  }, []);

  useEffect(() => {
    dispatch(getDraftRoomV2Players());
  }, [dispatch]);

  useEffect(() => {
    const syncAccessToken = () => {
      const nextToken = getCurrentAccessToken();
      setDraftAccessToken((prevToken) => (prevToken === nextToken ? prevToken : nextToken));
    };

    syncAccessToken();

    const intervalId = setInterval(syncAccessToken, 2000);
    window.addEventListener("focus", syncAccessToken);
    window.addEventListener("storage", syncAccessToken);
    document.addEventListener("visibilitychange", syncAccessToken);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener("focus", syncAccessToken);
      window.removeEventListener("storage", syncAccessToken);
      document.removeEventListener("visibilitychange", syncAccessToken);
    };
  }, []);

  useEffect(() => {
    const tokenFromUserState = userInfo?.tokens?.access ?? null;
    if (!tokenFromUserState) return;
    setDraftAccessToken((prevToken) => (
      prevToken === tokenFromUserState ? prevToken : tokenFromUserState
    ));
  }, [userInfo?.tokens?.access]);

  useEffect(() => {
    queuedPlayerIdsRef.current = queuedPlayerIds;
  }, [queuedPlayerIds]);

  useEffect(() => {
    setAutoPickByTeamId({});
  }, [eventId]);

  useEffect(() => {
    setHasSelectingStarted(false);
  }, [eventId]);

  const resolvePickByIndex = useCallback((overallIndex) => {
    if (overallIndex == null) return null;
    const pickListSnapshot = pickListRef.current || [];
    const normalizedIndex = Number(overallIndex);
    if (!Number.isFinite(normalizedIndex)) return null;
    return pickListSnapshot.find(
      (pick) => Number(pick.id) === normalizedIndex
    ) || null;
  }, []);

  const upsertDraftedEntries = useCallback((entries) => {
    if (!entries || !entries.length) return;
    setDrafted((prev) => {
      const nextMap = new Map(prev.map((entry) => [entry.pickId, entry]));
      entries.forEach((entry) => {
        if (!entry || entry.pickId == null) return;
        const existing = nextMap.get(entry.pickId) || {};
        nextMap.set(entry.pickId, { ...existing, ...entry });
      });
      return Array.from(nextMap.values());
    });
  }, []);

  const buildDraftedEntry = useCallback((payload) => {
    const pickPayload = normalizePickPayload(payload);
    if (!pickPayload) return null;
    const overallIndex = getPickIndexValue(pickPayload);
    if (!overallIndex) return null;
    const fallbackPick = resolvePickByIndex(overallIndex);
    const teamId = getPickTeamId(pickPayload) ?? fallbackPick?.teamId ?? null;
    const teamName = getPickTeamName(pickPayload) ?? fallbackPick?.team ?? null;
    const roundNumber = getPickRoundNumber(pickPayload) ?? fallbackPick?.roundNumber ?? null;
    const pickInRound = getPickInRoundValue(pickPayload) ?? fallbackPick?.pickInRound ?? null;
    const playerId =
      pickPayload?.player_id
      ?? pickPayload?.playerId
      ?? pickPayload?.player?.id
      ?? null;
    const rawPlayerName =
      pickPayload?.player_name
      ?? pickPayload?.playerName
      ?? pickPayload?.player?.player
      ?? pickPayload?.player?.name
      ?? pickPayload?.player
      ?? null;
    const playerName = typeof rawPlayerName === "string" ? rawPlayerName : null;
    if (!playerId && !playerName) return null;
    return {
      pickId: overallIndex,
      teamId,
      teamName: teamName || "Team",
      playerId,
      playerName: playerName || "Player",
      isAuto: Boolean(pickPayload?.is_auto ?? pickPayload?.auto_pick ?? false),
      teamScore: pickPayload?.team_score ?? null,
      ranking: pickPayload?.ranking ?? pickPayload?.player?.ranking ?? null,
      position: pickPayload?.position ?? pickPayload?.player?.position ?? null,
      roundNumber,
      pickInRound,
    };
  }, [resolvePickByIndex]);

  const applyPickPayload = useCallback((payload) => {
    const entry = buildDraftedEntry(payload);
    if (!entry) return;
    const draftedPlayerId = toNumber(entry.playerId);
    if (draftedPlayerId != null) {
      setQueuedPlayerIds((prev) => {
        const nextQueue = prev.filter((queuedId) => queuedId !== draftedPlayerId);
        queuedPlayerIdsRef.current = nextQueue;
        return nextQueue;
      });
    }
    upsertDraftedEntries([entry]);
  }, [buildDraftedEntry, upsertDraftedEntries]);

  useEffect(() => {
    if (!livePickDeadlineAt) {
      setLiveSecondsRemaining(0);
      return;
    }
    const deadline = getUtcTimestamp(livePickDeadlineAt);
    if (!deadline) {
      setLiveSecondsRemaining(0);
      return;
    }
    const updateRemaining = () => {
      const diffSeconds = Math.max(Math.floor((deadline - Date.now()) / 1000), 0);
      setLiveSecondsRemaining(diffSeconds);
    };
    updateRemaining();
    const interval = setInterval(updateRemaining, 1000);
    return () => clearInterval(interval);
  }, [livePickDeadlineAt]);

  useEffect(() => {
    let isMounted = true;
    const fetchTeams = async () => {
      setTeamsLoading(true);
      try {
        const response = await axiosInstance.get(
          `${API_ENDPOINT}trade-value-history/?limit=1000&offset=0&round=&round_index_number=${totalRounds}&tm=`
        );
        const teams = response?.data?.results || [];
        if (isMounted) {
          setSimulatorPicks(teams);
        }
        const teamSet = [];
        teams.forEach((team) => {
          const teamRound = team.round;
          const exists = teamSet.some((entry) => entry.name === teamRound.name);
          if (!exists) {
            teamSet.push({
              id: teamRound.id,
              name: teamRound.name,
              logo: teamRound.logo,
              selection: teamRound.index,
              draftCountId: team.team_neads_info.id,
            });
          }
        });

        const orderedTeams = teamSet
          .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
        if (isMounted) {
          setTeamList(orderedTeams);
        }
      } catch (error) {
        if (isMounted) {
          setTeamList([]);
          setSimulatorPicks([]);
        }
      } finally {
        if (isMounted) {
          setTeamsLoading(false);
        }
      }
    };
    fetchTeams();
    return () => {
      isMounted = false;
    };
  }, [totalRounds]);

  useEffect(() => {
    if (drafted.length !== 0) return;
    setCurrentPickIndex(0);
    setIsDraftComplete(false);
  }, [totalRounds, drafted.length]);

  useEffect(() => {
    if (!eventId) {
      setTotalRounds(4);
      setIsFinishedEventModalOpen(false);
      return;
    }
    let isMounted = true;
    setIsFinishedEventModalOpen(false);
    const fetchEventConfig = async () => {
      try {
        const response = await axiosInstance.get(
          `${API_ENDPOINT}event-config/${eventId}/`
        );
        const data = response?.data;
        const fetchedRounds = Number(data?.rounds);
        const resolvedRounds =
          Number.isFinite(fetchedRounds) && fetchedRounds > 0
            ? fetchedRounds
            : 4;
        if (isMounted) {
          setTotalRounds(resolvedRounds);
          setLiveStatus(data?.status ?? null);
          setLiveCurrentPickIndex(data?.current_pick_index ?? null);
          setLivePickDeadlineAt(data?.pick_deadline_at ?? null);
          if (data?.status === "completed" || data?.status === "finished") {
            setIsFinishedEventModalOpen(true);
          }
        }
      } catch (error) {
        if (isMounted) {
          setTotalRounds(4);
        }
      }
    };
    fetchEventConfig();
    return () => {
      isMounted = false;
    };
  }, [eventId]);

  useEffect(() => {
    if (isDraftComplete && !isFinishedEventModalOpen) {
      setIsDraftCompleteModalOpen(true);
    }
  }, [isDraftComplete, isFinishedEventModalOpen]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (!eventId) {
      setEventPicks([]);
      return;
    }
    let isMounted = true;
    const fetchPicks = async () => {
      setPicksLoading(true);
      try {
        const response = await axiosInstance.get(
          `${API_ENDPOINT}event-config/${eventId}/picks/`
        );
        const data = response?.data;
        const picks = Array.isArray(data) ? data : (data?.results || []);
        if (isMounted) {
          setEventPicks(picks);
          const draftedEntries = picks
            .map((pick) => buildDraftedEntry(pick))
            .filter(Boolean);
          if (draftedEntries.length) {
            upsertDraftedEntries(draftedEntries);
          }
        }
      } catch (error) {
        if (isMounted) {
          setEventPicks([]);
        }
      } finally {
        if (isMounted) {
          setPicksLoading(false);
        }
      }
    };
    fetchPicks();
    return () => {
      isMounted = false;
    };
  }, [eventId, buildDraftedEntry, upsertDraftedEntries]);

  useEffect(() => {
    let isMounted = true;
    const fetchSlots = async () => {
      if (!eventId) return;
      setSlotsLoading(true);
      try {
        const response = await axiosInstance.get(
          `${API_ENDPOINT}event-config/${eventId}/slots/`
        );
        if (isMounted) {
          setSlots(response.data || []);
        }
      } catch (error) {
        if (isMounted) {
          setSlots([]);
        }
      } finally {
        if (isMounted) {
          setSlotsLoading(false);
        }
      }
    };
    fetchSlots();
    return () => {
      isMounted = false;
    };
  }, [eventId]);

  const teamById = useMemo(() => {
    const entries = teamList.map((team) => [team.id, team]);
    return new Map(entries);
  }, [teamList]);

  const teamNeedsById = useMemo(() => {
    const needsMap = new Map();

    simulatorPicks.forEach((item) => {
      const teamId = item?.round?.id;
      if (!teamId || !Array.isArray(item?.team_neads_info?.team_neads_info)) {
        return;
      }
      const sortedNeeds = item.team_neads_info.team_neads_info
        .slice()
        .sort((a, b) => getPriorityValue(a?.priority) - getPriorityValue(b?.priority));

      const positions = [];
      const seen = new Set();
      sortedNeeds.forEach((needs) => {
        (needs?.positions || []).forEach((pos) => {
          const name = pos?.name;
          if (name && !seen.has(name)) {
            seen.add(name);
            positions.push(name);
          }
        });
      });
      if (positions.length) {
        needsMap.set(teamId, positions);
      }
    });
    return needsMap;
  }, [simulatorPicks]);


  const eventPickList = useMemo(() => {
    if (!eventPicks.length) return [];
    const slotsByTeamId = new Map(
      slots.map((slot) => [slot.team_id, slot])
    );
    const slotsByTeamName = new Map(
      slots.map((slot) => [slot.team_name, slot])
    );
    return eventPicks
      .map((pick) => {
        const pickId = getPickIndexValue(pick);
        if (!pickId) return null;
        const roundNumber = getPickRoundNumber(pick);
        const pickInRound = getPickInRoundValue(pick);
        const teamId = getPickTeamId(pick);
        const teamName = getPickTeamName(pick) || "Team";
        const slot = teamId != null
          ? slotsByTeamId.get(teamId)
          : slotsByTeamName.get(teamName);
        return {
          id: pickId,
          overallIndex: pickId,
          team: teamName,
          teamId,
          userId: slot?.user_id ?? null,
          roundNumber,
          pickInRound,
        };
      })
      .filter(Boolean)
      .sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
  }, [eventPicks, slots]);

  const pickList = useMemo(
    () => eventPickList,
    [eventPickList]
  );

  useEffect(() => {
    pickListRef.current = pickList;
  }, [pickList]);

  const isLoadingDraft = isPlayersLoading
    || teamsLoading
    || slotsLoading
    || picksLoading
    || !pickList.length;

  useEffect(() => {
    if (!eventId || isLoadingDraft) return undefined;
    const wsUrl = getDraftWebSocketUrl(eventId, draftAccessToken);
    if (!wsUrl) {
      console.warn("[DraftRoomV2] invalid draft websocket url", {
        eventId,
        hasToken: Boolean(draftAccessToken),
      });
      setIsSocketConnected(false);
      return undefined;
    }

    let disposed = false;
    shouldReconnectDraftWsRef.current = true;
    draftWsReconnectAttemptRef.current = 0;

    const clearReconnectTimer = () => {
      if (!draftWsReconnectTimerRef.current) return;
      clearTimeout(draftWsReconnectTimerRef.current);
      draftWsReconnectTimerRef.current = null;
    };

    const handleDraftEvent = (rawPayload) => {
      if (!rawPayload) return;
      const draftMessage =
        rawPayload?.type === "draft_event" || rawPayload?.event === "draft_event"
          ? rawPayload?.data ?? rawPayload?.payload ?? {}
          : rawPayload;
      const eventType = draftMessage?.type;
      const eventPayload = draftMessage?.data ?? draftMessage?.payload ?? draftMessage;
      const payloadEventId =
        rawPayload?.event_config_id
        ?? rawPayload?.eventConfigId
        ?? rawPayload?.event_id
        ?? rawPayload?.eventId
        ?? draftMessage?.event_config_id
        ?? draftMessage?.eventConfigId
        ?? draftMessage?.event_id
        ?? draftMessage?.eventId
        ?? eventPayload?.event_config_id
        ?? eventPayload?.eventConfigId
        ?? eventPayload?.event_id
        ?? eventPayload?.eventId
        ?? null;
      if (payloadEventId && String(payloadEventId) !== String(eventId)) {
        return;
      }

      switch (eventType) {
        case "TEST": {
          console.log("[DraftRoomV2] draft_event:TEST", eventPayload);
          break;
        }
        case "DRAFT_STATE": {
          console.log("[DraftRoomV2] draft_event:DRAFT_STATE", eventPayload);
          const statePayload = eventPayload?.state ?? eventPayload ?? {};
          const nextStatus = statePayload.status ?? statePayload.phase ?? null;
          setLiveStatus(nextStatus);
          setLiveCurrentPickIndex(
            statePayload.current_pick_index ?? statePayload.currentPickIndex ?? null
          );
          setLivePickDeadlineAt(
            statePayload.pick_deadline_at ?? statePayload.pickDeadlineAt ?? null
          );
          if (nextStatus === "completed" || nextStatus === "finished") {
            //setIsDraftComplete(true);
          }
          break;
        }
        case "SELECTING": {
          console.log("[DraftRoomV2] draft_event:SELECTING", eventPayload);
          setHasSelectingStarted(true);
          const overallIndex = toNumber(
            eventPayload?.index
            ?? eventPayload?.overall_index
            ?? eventPayload?.overallIndex
          );
          const durationMs = toNumber(
            eventPayload?.durationMs
            ?? eventPayload?.duration_ms
            ?? eventPayload?.duration
          );
          if (overallIndex != null) {
            setLiveStatus("live");
            setLiveCurrentPickIndex(overallIndex);
          }
          if (durationMs != null && durationMs > 0) {
            const remainingSeconds = Math.max(Math.ceil(durationMs / 1000), 0);
            setLiveSecondsRemaining(remainingSeconds);
            setLivePickDeadlineAt(new Date(Date.now() + durationMs).toISOString());
          }
          break;
        }
        case "SELECTED": {
          console.log("[DraftRoomV2] draft_event:SELECTED", eventPayload);
          submitPickInFlightRef.current = false;
          setIsMakingPick(false);
          const selectedPlayerId = toNumber(eventPayload?.playerId);
          if (selectedPlayerId != null) {
            setQueuedPlayerIds((prev) => {
              const nextQueue = prev.filter((queuedId) => queuedId !== selectedPlayerId);
              queuedPlayerIdsRef.current = nextQueue;
              return nextQueue;
            });
          }
          applyPickPayload({
            overall_index: eventPayload?.overall_index ?? null,
            team_id: eventPayload?.teamId ?? null,
            player_id: selectedPlayerId,
            player_name: eventPayload?.playerName ?? null,
            position: eventPayload?.position ?? null,
            ranking: eventPayload?.ranking ?? null,
          });
          break;
        }
        case "PICK_MADE": {
          console.log("[DraftRoomV2] draft_event:PICK_MADE", eventPayload);
          submitPickInFlightRef.current = false;
          setIsMakingPick(false);
          applyPickPayload(eventPayload);
          break;
        }
        case "CLOCK": {
          const clockPayload = eventPayload?.clock ?? eventPayload ?? {};
          console.log("[DraftRoomComponentV2] draft_event:CLOCK", clockPayload);
          if (clockPayload?.phase != null || clockPayload?.status != null) {
            setLiveStatus(clockPayload.phase ?? clockPayload.status ?? null);
          }
          const remainingMs = toNumber(
            clockPayload?.remainingMs
            ?? clockPayload?.remaining_ms
            ?? clockPayload?.remaining
          );
          if (remainingMs != null) {
            const remainingSeconds = Math.max(Math.ceil(remainingMs / 1000), 0);
            setLiveSecondsRemaining(remainingSeconds);
            setLivePickDeadlineAt(new Date(Date.now() + remainingMs).toISOString());
          }
          break;
        }
        case "DRAFT_LIST": {
          const ackEventId = toNumber(eventPayload?.eventId);
          const ackTeamId = toNumber(eventPayload?.teamId);
          const currentEventId = toNumber(eventId);
          const currentTeamId = myTeamIdRef.current;
          if (
            ackEventId == null
            || ackTeamId == null
            || currentEventId == null
            || currentTeamId == null
          ) {
            break;
          }
          if (ackEventId !== currentEventId || ackTeamId !== currentTeamId) {
            break;
          }
          const serverQueue = normalizeQueuePlayerIds(eventPayload?.playerIds);
          queuedPlayerIdsRef.current = serverQueue;
          setQueuedPlayerIds(serverQueue);
          break;
        }
        case "AUTOPICK": {
          const autopickTeamId = toNumber(eventPayload?.teamId ?? eventPayload?.team_id);
          const autopickEnabled = toBoolean(eventPayload?.enabled);
          if (autopickTeamId == null || autopickEnabled == null) {
            break;
          }
          setAutoPickByTeamId((prev) => {
            if (prev[autopickTeamId] === autopickEnabled) {
              return prev;
            }
            return {
              ...prev,
              [autopickTeamId]: autopickEnabled,
            };
          });
          if (myTeamIdRef.current != null && autopickTeamId === myTeamIdRef.current) {
            setAutoEnabled(autopickEnabled);
          }
          break;
        }
        case "TRADE_CREATED":
        case "TRADE_UPDATED": {
          const trade = eventPayload?.trade ?? eventPayload;
          if (trade?.id != null) {
            setTradesById((prev) => {
              const next = new Map(prev);
              next.set(trade.id, trade);
              return next;
            });
          }
          break;
        }
        case "TRADE_LIST": {
          const trades = eventPayload?.trades;
          if (Array.isArray(trades)) {
            setTradesById((prev) => {
              const next = new Map(prev);
              trades.forEach((trade) => {
                if (trade?.id != null) {
                  next.set(trade.id, trade);
                }
              });
              return next;
            });
          }
          break;
        }
        case "TRADE_ERROR": {
          const detail = eventPayload?.detail || "Trade action failed";
          toast.error(detail);
          break;
        }
        default:
          break;
      }
    };

    const connectDraftWebSocket = () => {
      if (disposed || !shouldReconnectDraftWsRef.current) return;
      clearReconnectTimer();
      try {
        const ws = new WebSocket(wsUrl);
        draftWsRef.current = ws;

        ws.onopen = () => {
          if (disposed) return;
          draftWsReconnectAttemptRef.current = 0;
          setIsSocketConnected(true);
          const numericEventId = toNumber(eventId);
          sendDraftWsMessage({
            type: "join_draft",
            action: "join_draft",
            event_id: numericEventId ?? eventId,
            eventId: numericEventId ?? eventId,
            event_config_id: numericEventId ?? eventId,
            eventConfigId: numericEventId ?? eventId,
          });
          sendDraftWsMessage({
            type: "TRADE_LIST",
            eventId: numericEventId ?? eventId,
          });
          console.log("[DraftRoomV2] draft websocket connected", { eventId });
        };

        ws.onmessage = (event) => {
          if (disposed) return;
          let payload = null;
          try {
            payload = JSON.parse(event.data);
          } catch (error) {
            console.warn("[DraftRoomV2] invalid draft websocket payload", event.data);
            return;
          }
          handleDraftEvent(payload);
        };

        ws.onerror = (error) => {
          if (disposed) return;
          console.warn("[DraftRoomV2] draft websocket error", error);
        };

        ws.onclose = (closeEvent) => {
          if (draftWsRef.current === ws) {
            draftWsRef.current = null;
          }
          if (disposed) return;
          setIsSocketConnected(false);
          if (!shouldReconnectDraftWsRef.current) return;
          const nextAttempt = draftWsReconnectAttemptRef.current + 1;
          draftWsReconnectAttemptRef.current = nextAttempt;
          const delay = Math.min(1000 * (2 ** (nextAttempt - 1)), 10000);
          console.log("[DraftRoomV2] draft websocket disconnected, retrying", {
            code: closeEvent?.code,
            reason: closeEvent?.reason,
            nextAttempt,
            delay,
          });
          draftWsReconnectTimerRef.current = setTimeout(connectDraftWebSocket, delay);
        };
      } catch (error) {
        setIsSocketConnected(false);
        const nextAttempt = draftWsReconnectAttemptRef.current + 1;
        draftWsReconnectAttemptRef.current = nextAttempt;
        const delay = Math.min(1000 * (2 ** (nextAttempt - 1)), 10000);
        draftWsReconnectTimerRef.current = setTimeout(connectDraftWebSocket, delay);
      }
    };

    connectDraftWebSocket();

    return () => {
      disposed = true;
      shouldReconnectDraftWsRef.current = false;
      clearReconnectTimer();
      const activeSocket = draftWsRef.current;
      if (activeSocket) {
        draftWsRef.current = null;
        try {
          activeSocket.close();
        } catch (error) {
          // no-op
        }
      }
      setIsSocketConnected(false);
    };
  }, [eventId, isLoadingDraft, draftAccessToken, applyPickPayload, sendDraftWsMessage]);

  const mySlot = useMemo(() => {
    if (!userInfo?.id || !slots.length) return null;
    return slots.find((slot) => slot.user_id === userInfo.id) || null;
  }, [slots, userInfo]);

  useEffect(() => {
    myTeamIdRef.current = toNumber(mySlot?.team_id);
  }, [mySlot?.team_id]);

  const myTeamDetails = useMemo(() => {
    if (!mySlot) return null;
    const team =
      teamById.get(mySlot.team_id)
      || teamList.find((entry) => entry.name === mySlot.team_name)
      || null;
    return {
      logo: team?.logo || "",
      name: mySlot.team_name || team?.name || "Team",
      slot: mySlot.slot || null,
      teamId: mySlot.team_id || team?.id || null,
    };
  }, [mySlot, teamById, teamList]);

  useEffect(() => {
    if (isRosterTeamManual) return;
    if (mySlot?.team_id) {
      setSelectedRosterTeamId(mySlot.team_id);
      return;
    }
    if (!selectedRosterTeamId && teamList.length) {
      setSelectedRosterTeamId(teamList[0].id);
    }
  }, [mySlot, teamList, selectedRosterTeamId, isRosterTeamManual]);

  const rosterTeam = useMemo(() => {
    if (!selectedRosterTeamId) return null;
    return teamById.get(selectedRosterTeamId)
      || teamList.find((team) => team.id === selectedRosterTeamId)
      || null;
  }, [selectedRosterTeamId, teamById, teamList]);

  const myRoster = useMemo(() => {
    if (!selectedRosterTeamId) return [];
    return drafted
      .filter((entry) => entry.teamId === selectedRosterTeamId)
      .map((entry) => ({
        id: `${entry.playerId}-${entry.pickId}`,
        pos: entry.position || "-",
        player: entry.playerName || "-",
        rank: entry.ranking ?? "-",
      }));
  }, [drafted, selectedRosterTeamId]);

  const rosterTeamOptions = useMemo(
    () =>
      teamList.map((team) => ({
        value: team.id,
        label: team.name,
        logo: team.logo,
      })),
    [teamList]
  );

  const selectedRosterTeamOption = useMemo(() => {
    if (!selectedRosterTeamId) return null;
    return rosterTeamOptions.find((team) => team.value === selectedRosterTeamId) || null;
  }, [rosterTeamOptions, selectedRosterTeamId]);

  const rosterSelectStyles = useMemo(() => ({
    control: (base) => ({
      ...base,
      minHeight: 28,
      height: 28,
      borderColor: "#E9EBEF",
      boxShadow: "none",
      fontFamily: "'Saira Semi Condensed', sans-serif",
      fontSize: 12,
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 8px",
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: 28,
    }),
    menu: (base) => ({
      ...base,
      zIndex: 5,
    }),
  }), []);

  const myTeamId = myTeamDetails?.teamId ?? null;

  const tradesList = useMemo(
    () => Array.from(tradesById.values()).sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return tb - ta;
    }),
    [tradesById]
  );

  const pendingTrades = useMemo(
    () => tradesList.filter((t) => t.status === "pending"),
    [tradesList]
  );

  const historyTrades = useMemo(
    () => tradesList.filter((t) => t.status !== "pending"),
    [tradesList]
  );

  const sendTradePropose = useCallback(
    ({ counterpartyTeamId, proposerPlayerIds, counterpartyPlayerIds, note }) => {
      const numericEventId = toNumber(eventId);
      sendDraftWsMessage({
        type: "TRADE_PROPOSE",
        eventId: numericEventId,
        proposerTeamId: myTeamId,
        counterpartyTeamId,
        proposerPlayerIds,
        counterpartyPlayerIds,
        note: note || "",
      });
    },
    [eventId, myTeamId, sendDraftWsMessage]
  );

  const sendTradeAction = useCallback(
    (actionType, tradeId) => {
      const numericEventId = toNumber(eventId);
      sendDraftWsMessage({
        type: actionType,
        eventId: numericEventId,
        tradeId,
      });
    },
    [eventId, sendDraftWsMessage]
  );

  const positionOptions = useMemo(() => {
    const seen = new Set();
    playerResults.forEach((player) => {
      if (player?.position) {
        seen.add(player.position);
      }
    });
    return [
      ...Array.from(seen).map((position) => ({
        value: position,
        label: position,
        color: POSITIONS_COLOR[position] || "",
      })),
    ];
  }, [playerResults]);

  const selectedPositionOptions = useMemo(() => {
    if (!selectedPositions.length) {
      return [positionOptions[0]];
    }
    return positionOptions.filter((option) => selectedPositions.includes(option.value));
  }, [positionOptions, selectedPositions]);

  const positionSelectStyles = useMemo(() => ({
    control: (base) => ({
      ...base,
      minHeight: 30,
      height: 30,
      borderColor: "#E9EBEF",
      boxShadow: "none",
      fontSize: 14,
    }),
    multiValue: (styles, { data }) => {
      return {
        ...styles,
        fontSize: 14,
        backgroundColor: data.color,
      };
    },
    valueContainer: (base) => ({
      ...base,
      padding: "0 8px",
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: 28,
    }),
    menu: (base) => ({
      ...base,
      zIndex: 5,
    }),
  }), []);

  const myTeamNeeds = useMemo(() => {
    if (!mySlot?.team_id) return [];
    return teamNeedsById.get(mySlot.team_id) || [];
  }, [mySlot, teamNeedsById]);

  const draftedPlayerIds = useMemo(() => {
    const ids = new Set();
    drafted.forEach((entry) => {
      const playerId = toNumber(entry?.playerId);
      if (playerId != null) {
        ids.add(playerId);
      }
    });
    eventPicks.forEach((pick) => {
      const playerId = toNumber(
        pick?.selected_player_id
        ?? pick?.player_id
        ?? pick?.player?.id
      );
      if (playerId != null) {
        ids.add(playerId);
      }
    });
    return ids;
  }, [drafted, eventPicks]);
  const queuedPlayerIdSet = useMemo(
    () => new Set(queuedPlayerIds),
    [queuedPlayerIds]
  );
  const playersById = useMemo(() => {
    const map = new Map();
    playerResults.forEach((player) => {
      const playerId = toNumber(player?.id);
      if (playerId != null) {
        map.set(playerId, player);
      }
    });
    return map;
  }, [playerResults]);

  const resolvePlayerName = useCallback(
    (playerId) => {
      const player = playersById.get(toNumber(playerId));
      return player?.player || `Player #${playerId}`;
    },
    [playersById]
  );

  const queuedPlayers = useMemo(
    () => queuedPlayerIds.map((playerId) => {
      const player = playersById.get(playerId);
      return {
        id: playerId,
        ranking: player?.ranking ?? "-",
        player: player?.player ?? `Player #${playerId}`,
        position: player?.position ?? "-",
        school: player?.school ?? "-",
        bpa: player?.bpa ?? "-",
      };
    }),
    [queuedPlayerIds, playersById]
  );

  const draftedPickIds = useMemo(
    () => new Set(drafted.map((entry) => entry.pickId)),
    [drafted]
  );

  const draftedByRound = useMemo(() => {
    const grouped = new Map();
    const sorted = [...drafted].sort((a, b) => a.pickId - b.pickId);
    sorted.forEach((entry) => {
      const round = entry.roundNumber || 1;
      if (!grouped.has(round)) {
        grouped.set(round, []);
      }
      grouped.get(round).push(entry);
    });
    return Array.from(grouped.entries()).sort((a, b) => a[0] - b[0]);
  }, [drafted]);

  const hasServerState =
    liveStatus !== null
    || liveCurrentPickIndex !== null
    || livePickDeadlineAt !== null;

  const serverPickListIndex = useMemo(() => {
    if (liveCurrentPickIndex == null || !pickList.length) return null;
    const target = Number(liveCurrentPickIndex);
    if (!Number.isFinite(target)) return null;
    const resolvedIndex = pickList.findIndex(
      (pick) => Number(pick.overallIndex ?? pick.id) === target
    );
    return resolvedIndex >= 0 ? resolvedIndex : null;
  }, [liveCurrentPickIndex, pickList]);

  const activePickIndex = serverPickListIndex ?? currentPickIndex;
  const currentPick = pickList[activePickIndex];
  const isMyPick = Boolean(currentPick?.userId && currentPick.userId === userInfo?.id);
  const showManualDraft = isMyPick && !autoEnabled;

  const setAutoPickEnabled = useCallback((nextEnabled) => {
    setAutoEnabled(nextEnabled);
    const teamId = toNumber(mySlot?.team_id);
    if (teamId != null) {
      setAutoPickByTeamId((prev) => ({
        ...prev,
        [teamId]: nextEnabled,
      }));
    }
    const eventConfigId = toNumber(eventId) ?? eventId;
    if (teamId == null || !eventConfigId) {
      return;
    }
    const payload = {
      type: "set_autopick",
      action: "set_autopick",
      eventId: eventConfigId,
      event_id: eventConfigId,
      eventConfigId: eventConfigId,
      event_config_id: eventConfigId,
      teamId,
      team_id: teamId,
      enabled: nextEnabled,
    };
    const sent = sendDraftWsMessage(payload);
    console.log("[DraftRoomV2] set_autopick", {
      ...payload,
      sent,
    });
  }, [eventId, mySlot?.team_id, sendDraftWsMessage]);

  const handleAutoPickToggle = useCallback((event) => {
    setAutoPickEnabled(Boolean(event?.target?.checked));
  }, [setAutoPickEnabled]);

  const syncDraftListQueue = useCallback((nextPlayerIds) => {
    const normalizedEventId = toNumber(eventId);
    const normalizedTeamId = toNumber(mySlot?.team_id);
    if (normalizedEventId == null || normalizedTeamId == null) {
      return false;
    }
    return sendDraftWsMessage({
      type: "DRAFT_LIST",
      eventId: normalizedEventId,
      teamId: normalizedTeamId,
      playerIds: normalizeQueuePlayerIds(nextPlayerIds),
    });
  }, [eventId, mySlot?.team_id, sendDraftWsMessage]);

  const commitClientQueueChange = useCallback((nextQueue) => {
    const normalizedNextQueue = normalizeQueuePlayerIds(nextQueue);
    const prevQueue = queuedPlayerIdsRef.current;
    if (areNumberArraysEqual(prevQueue, normalizedNextQueue)) {
      return;
    }
    queuedPlayerIdsRef.current = normalizedNextQueue;
    setQueuedPlayerIds(normalizedNextQueue);
    syncDraftListQueue(normalizedNextQueue);
  }, [syncDraftListQueue]);

  const submitPick = useCallback(({ overallIndex, playerId, source }) => {
    const normalizedEventId = toNumber(eventId);
    const normalizedPlayerId = toNumber(playerId);
    if (normalizedEventId == null || normalizedPlayerId == null) {
      console.warn("[DraftRoomV2] submitPick prevented (missing data)", {
        overallIndex,
        playerId,
        source,
      });
      return;
    }
    if (submitPickInFlightRef.current || isMakingPick) {
      console.warn("[DraftRoomV2] submitPick prevented duplicate", {
        overallIndex,
        playerId: normalizedPlayerId,
        source,
      });
      return;
    }

    submitPickInFlightRef.current = true;
    setIsMakingPick(true);

    const sent = sendDraftWsMessage({
      type: "SELECT",
      eventId: normalizedEventId,
      playerId: normalizedPlayerId,
    });

    if (!sent) {
      submitPickInFlightRef.current = false;
      setIsMakingPick(false);
      if (source === "manual") {
        toast.error("Socket disconnected. Please try again.");
      }
      return;
    }

    console.log("[DraftRoomV2] pick submitted via websocket", {
      overallIndex,
      eventId: normalizedEventId,
      playerId: normalizedPlayerId,
      source,
    });
  }, [
    eventId,
    isMakingPick,
    sendDraftWsMessage,
  ]);

  const addToQueue = useCallback((player) => {
    const playerId = toNumber(player?.id);
    if (playerId == null || draftedPlayerIds.has(playerId)) {
      return;
    }
    const prevQueue = queuedPlayerIdsRef.current;
    if (prevQueue.includes(playerId)) {
      return;
    }
    commitClientQueueChange([...prevQueue, playerId]);
  }, [draftedPlayerIds, commitClientQueueChange]);

  const removeFromQueue = useCallback((playerId) => {
    const normalizedPlayerId = toNumber(playerId);
    if (normalizedPlayerId == null) {
      return;
    }
    const nextQueue = queuedPlayerIdsRef.current.filter(
      (queuedPlayerId) => queuedPlayerId !== normalizedPlayerId
    );
    commitClientQueueChange(nextQueue);
  }, [commitClientQueueChange]);

  const handleQueueDragEnd = useCallback((result) => {
    if (!result.destination) return;
    const { source, destination } = result;
    if (source.index === destination.index) return;
    const nextQueue = [...queuedPlayerIdsRef.current];
    const [moved] = nextQueue.splice(source.index, 1);
    if (moved == null) {
      return;
    }
    nextQueue.splice(destination.index, 0, moved);
    commitClientQueueChange(nextQueue);
  }, [commitClientQueueChange]);

  const draftFromList = useCallback((player) => {
    console.log("draftFromList...");

    if (!player || !currentPick) {
      return;
    }
    if (!showManualDraft || isMakingPick) {
      return;
    }
    const normalizedPlayerId = toNumber(player.id);
    if (normalizedPlayerId == null) {
      return;
    }
    if (draftedPickIds.has(currentPick.id) || draftedPlayerIds.has(normalizedPlayerId)) {
      console.warn("[DraftRoomV2] manual pick prevented duplicate", {
        overallIndex: currentPick.overallIndex ?? currentPick.id,
        playerId: normalizedPlayerId,
      });
      return;
    }
    const overallIndex = liveCurrentPickIndex ?? currentPick.overallIndex ?? currentPick.id;
    submitPick({ overallIndex, playerId: normalizedPlayerId, source: "manual" });
  }, [
    showManualDraft,
    currentPick,
    draftedPickIds,
    draftedPlayerIds,
    isMakingPick,
    submitPick,
    liveCurrentPickIndex,
  ]);

  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (hasServerState) {
      return undefined;
    }
    if (isDraftComplete || pickList.length === 0) {
      return undefined;
    }
    const nextSeconds = currentPickIndex === 0
      ? 30
      : (currentPick?.userId
        ? (currentPick.userId === userInfo?.id && autoEnabled ? 1 : 60)
        : 1);
    setSecondsLeft(nextSeconds);
    timerRef.current = setInterval(() => {
      setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [
    currentPickIndex,
    isDraftComplete,
    pickList.length,
    autoEnabled,
    userInfo?.id,
    hasServerState,
  ]);

  useEffect(() => {
    if (isDraftComplete) {
      commitClientQueueChange([]);
    }
  }, [isDraftComplete, commitClientQueueChange]);

  useEffect(() => {
    if (!pickList.length) return;
    if (drafted.length && drafted.length >= pickList.length) {
      setIsDraftComplete(true);
    }
  }, [drafted.length, pickList.length]);

  useEffect(() => {
    const currentPick = pickList[activePickIndex];
    if (!currentPick || isDraftComplete) {
      return;
    }
    console.log(
      `[DraftRoomComponentV2] Pick start: #${currentPick.id} (teamId ${currentPick.teamId}, ${currentPick.team})`
    );
  }, [activePickIndex, isDraftComplete, pickList]);

  const pickTiles = useMemo(() => {
    if (!pickList.length) {
      return [];
    }
    const tiles = [];
    const picksByRound = new Map();
    pickList.forEach((pickItem) => {
      const round = pickItem.roundNumber || 1;
      if (!picksByRound.has(round)) {
        picksByRound.set(round, []);
      }
      picksByRound.get(round).push(pickItem);
    });
    const roundIsComplete = (roundNumber) => {
      const roundPicks = picksByRound.get(roundNumber) || [];
      return roundPicks.every((pickItem) => draftedPickIds.has(pickItem.id));
    };
    let currentRound = null;
    pickList.forEach((pickItem) => {
      const round = pickItem.roundNumber || 1;
      if (currentRound !== round) {
        currentRound = round;
        if (!roundIsComplete(round)) {
          tiles.push({ type: "round", round });
        }
      }
      if (draftedPickIds.has(pickItem.id)) {
        return;
      }
      const tileTeamId = toNumber(pickItem.teamId);
      const teamAutoEnabled = tileTeamId != null
        ? autoPickByTeamId[tileTeamId]
        : null;
      tiles.push({
        type: "pick",
        pick: pickItem.overallIndex,
        team: pickItem.team,
        round,
        pickInRound: pickItem.pickInRound || null,
        logo: teamById.get(pickItem.teamId)?.logo || "",
        auto: teamAutoEnabled != null
          ? teamAutoEnabled
          : (!pickItem.userId || (autoEnabled && pickItem.userId === userInfo?.id)),
        user: Boolean(pickItem.userId),
        own: pickItem.userId === userInfo?.id,
      });
    });
    return tiles;
  }, [
    pickList,
    draftedPickIds,
    teamById,
    autoPickByTeamId,
    autoEnabled,
    userInfo?.id,
  ]);

  const draftedPicks = useMemo(() => {
    if (!pickList.length) {
      return [];
    }
    return [...drafted].sort((a, b) => b.pickId - a.pickId).map((entry) => {
      const round = entry.roundNumber || 1;
      const teamName = entry.teamName || `Team ${entry.teamId}`;
      const teamLogo = teamById.get(entry.teamId)?.logo || "";
      return {
        name: entry.playerName,
        logo: teamLogo,
        meta: `R${round}, P${entry.pickId} - ${teamName}`,
      };
    });
  }, [drafted, pickList.length, teamById]);

  const filteredResults = useMemo(() => {
    const normalizedQuery = playerSearchQuery.trim().toLowerCase();
    return playerResults.filter((player) => {
      const normalizedPlayerId = toNumber(player?.id);
      if (!showDrafted && normalizedPlayerId != null && draftedPlayerIds.has(normalizedPlayerId)) {
        return false;
      }
      if (!selectedPositions.includes("all") && !selectedPositions.includes(player.position)) {
        return false;
      }
      if (!normalizedQuery) {
        return true;
      }
      return (player.player || "").toLowerCase().includes(normalizedQuery);
    });
  }, [playerResults, playerSearchQuery, showDrafted, draftedPlayerIds, selectedPositions]);

  const displaySecondsLeft = hasServerState
    ? liveSecondsRemaining
    : secondsLeft;
  const formattedSecondsLeft = `00:${String(displaySecondsLeft).padStart(2, "0")}`;
  const currentRound = currentPick?.roundNumber || 1;
  const currentTeamDetails = currentPick
    ? teamById.get(currentPick.teamId)
    : null;
  const currentTeamName = currentPick?.team || "Team";
  const currentTeamLogo = currentTeamDetails?.logo || "";
  const displayPickNumber = liveCurrentPickIndex != null
    ? liveCurrentPickIndex
    : (currentPick?.overallIndex ?? currentPick?.id ?? 1);
  const pickRoundLabel = currentPick?.pickInRound
    ? ` (R${currentRound} · P${currentPick.pickInRound})`
    : (currentRound ? ` (R${currentRound})` : "");
  const isFinished = liveStatus === "completed" || liveStatus === "finished";
  const statusLabel = liveStatus
    && !["waiting", "live", "completed", "finished"].includes(String(liveStatus))
    ? `STATUS: ${String(liveStatus).toUpperCase()}`
    : null;
  const timerLabel = statusLabel || (
    isLoadingDraft
      ? "LOADING"
      : isFinished
        ? "COMPLETED"
        : !hasSelectingStarted
          ? "DRAFTING IN"
          : `RND ${currentRound} OF ${totalRounds}`
  );
  const showOnTheClock = !isDraftComplete;
  const showAutopickStatus = !isDraftComplete && autoEnabled;
  const showGreenStatus = showAutopickStatus || isDraftComplete;
  const nextMyPick = useMemo(() => {
    if (!myTeamDetails?.teamId || !pickList.length) return null;
    const safeActivePickIndex = Math.min(
      Math.max(Number(activePickIndex) || 0, 0),
      pickList.length - 1
    );
    for (let index = safeActivePickIndex; index < pickList.length; index += 1) {
      const pick = pickList[index];
      const pickId = Number(pick?.overallIndex ?? pick?.id);
      if (!Number.isFinite(pickId)) {
        continue;
      }
      if (pick.teamId !== myTeamDetails.teamId) {
        continue;
      }
      if (draftedPickIds.has(pickId)) {
        continue;
      }
      return {
        picksAway: Math.max(index - safeActivePickIndex, 0),
        roundNumber: pick.roundNumber ?? null,
        pickInRound: pick.pickInRound ?? null,
        overallIndex: pickId,
      };
    }
    return null;
  }, [myTeamDetails?.teamId, pickList, activePickIndex, draftedPickIds]);
  const nextMyPickTitle = nextMyPick
    ? (nextMyPick.picksAway === 0
      ? "You are on the clock!"
      : `You're on the clock in: ${nextMyPick.picksAway} Picks`)
    : "No upcoming picks";
  const nextMyPickSubtitle = nextMyPick
    ? `Round ${nextMyPick.roundNumber ?? "-"}, Pick ${nextMyPick.pickInRound ?? nextMyPick.overallIndex ?? "-"}`
    : "You have no remaining picks in this draft.";
  const modalDraftRows = useMemo(() => {
    const myTeamId = toNumber(mySlot?.team_id ?? myTeamDetails?.teamId);
    if (myTeamId == null) return [];
    const playerSchoolById = new Map(
      playerResults.map((player) => [String(player?.id), player?.school || "-"])
    );
    return [...drafted]
      .filter((entry) => toNumber(entry?.teamId) === myTeamId)
      .sort((a, b) => Number(a?.pickId ?? 0) - Number(b?.pickId ?? 0))
      .map((entry) => ({
        key: `${entry?.pickId}-${entry?.playerId ?? entry?.playerName}`,
        roundNumber: entry?.roundNumber ?? "-",
        pickNumber: entry?.pickId ?? "-",
        playerName: entry?.playerName ?? "-",
        school: playerSchoolById.get(String(entry?.playerId)) || "-",
        position: entry?.position ?? "-",
        logo: teamById.get(entry?.teamId)?.logo || "",
      }));
  }, [myTeamDetails?.teamId, mySlot?.team_id, drafted, playerResults, teamById]);
  const closeDraftCompleteModal = useCallback(() => {
    setIsDraftCompleteModalOpen(false);
  }, []);
  const copyDraftResultLink = useCallback(async () => {
    const shareLink = window.location.href;
    try {
      await navigator.clipboard.writeText(shareLink);
      toast.success("Link copied");
    } catch (error) {
      toast.error("Unable to copy link");
    }
  }, []);

  if (isFinishedEventModalOpen) {
    return (
      <DraftRoomV2Wrapper>
        <DraftCompleteModalOverlay>
          <div className="event-finished-modal">
            <h2>The event you&apos;re trying to access has already finished.</h2>
            <div className="divider" />
            <p>
              Go to the{" "}
              <button
                type="button"
                className="home-link"
                onClick={() => navigate(HOME)}
              >
                Draft Professor Homepage.
              </button>
            </p>
          </div>
        </DraftCompleteModalOverlay>
      </DraftRoomV2Wrapper>
    );
  }

  return (
    <DraftRoomV2Wrapper>
      {(isLoadingDraft || !isSocketConnected) && (
        <div className="draft-loading">
          <div className="logo">
            <img src={nflLogo} alt="NFL Fantasy" />
          </div>
          <div className="dots">
            <span />
            <span />
            <span />
            <span />
            <span />
          </div>
          <div className="text">Loading your draft</div>
        </div>
      )}
      <DraftTopBar>
        <TopBarRow>
          <TimerBox>
            {!isDraftComplete && <div className="label">{timerLabel}</div>}
            <div className="time">{isDraftComplete ? "--:--" : formattedSecondsLeft}</div>
          </TimerBox>

          {showOnTheClock && (
            <OnTheClockCard
              className="current-pick-module-container clr-white"
              title={currentTeamName}
            >
              <div className="contents">
                <div
                  data-testid="croppable-image"
                  className="croppable-image team-logo fl team-logo-vector"
                  style={{ height: 45, overflow: "hidden", width: 45 }}
                >
                  {currentTeamLogo ? (
                    <img
                      alt=""
                      className="Image team-logo fl team-logo-vector"
                      src={currentTeamLogo}
                    />
                  ) : null}
                </div>
                <div className="on-the-clock ttu">
                  On the Clock: Pick {displayPickNumber}
                </div>
                <div className="team-name">{currentTeamName}</div>
              </div>
            </OnTheClockCard>
          )}
          <PickStrip>
            {pickTiles.map((tile) => {
              if (tile.type === "round") {
                return (
                  <PickTile key={`round-${tile.round}`} className="round-tile">
                    <div className="round-label">ROUND</div>
                    <div className="round-number">{tile.round}</div>
                  </PickTile>
                );
              }
              return (
                <PickTile
                  key={tile.pick}
                  className={`${tile.auto ? "autopick" : ""} ${
                    tile.own ? "own-pick" : ""
                  }`}
                >
                  <div className="pick-label">Pick {tile.pick}</div>
                  {tile.auto && <div className="auto-word">AUTO</div>}
                  {tile.user && <div className="user-word"></div>}
                  <div className="pick-logo">
                    {tile.logo ? (
                      <img src={tile.logo} alt={`${tile.team} logo`} />
                    ) : null}
                  </div>
                  <div className="pick-team">{tile.team}</div>
                </PickTile>
              );
            })}
          </PickStrip>
        </TopBarRow>
      </DraftTopBar>

      <DraftRoomV2Grid>
        <Panel>
          <PanelHeader>
            <span>Pick Queue</span>
            <ToggleRow>
              <span>Autopick</span>
              <input
                type="checkbox"
                checked={autoEnabled}
                onChange={handleAutoPickToggle}
              />
            </ToggleRow>
          </PanelHeader>
          <PanelBody>
            <DragDropContext onDragEnd={handleQueueDragEnd}>
            <MiniTable>
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Player</th>
                  <th></th>
                </tr>
              </thead>
              <Droppable droppableId="queue">
                {(provided) => (
                  <tbody ref={provided.innerRef} {...provided.droppableProps}>
                    {queuedPlayers.length === 0 && (
                      <tr>
                        <td colSpan="3">No players in queue</td>
                      </tr>
                    )}
                    {queuedPlayers.map((player, index) => (
                      <Draggable
                        key={player.id}
                        draggableId={String(player.id)}
                        index={index}
                      >
                        {(dragProvided) => (
                          <tr
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                            className="queue-row"
                          >
                            <td>{player.ranking ?? "-"}</td>
                            <td>{player.player}</td>
                            <td className="queue-actions">
                              {showManualDraft ? (
                                <button
                                  className="draft-btn"
                                  disabled={isMakingPick || draftedPlayerIds.has(toNumber(player.id))}
                                  onClick={() => draftFromList(player)}
                                >
                                  Draft
                                </button>
                              ) : (
                                <button
                                  className="queue-btn"
                                  onClick={() => removeFromQueue(player.id)}
                                >
                                  Remove
                                </button>
                              )}
                            </td>
                          </tr>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </tbody>
                )}
              </Droppable>
            </MiniTable>
            </DragDropContext>
          </PanelBody>



          <PanelHeader>
            <span>Roster</span>
            <div style={{ marginLeft: "auto", minWidth: 160 }}>
              <Select
                inputId="roster-team-select"
                aria-label="Select roster team"
                value={selectedRosterTeamOption}
                options={rosterTeamOptions}
                isSearchable={false}
                onChange={(option) => {
                  setIsRosterTeamManual(true);
                  setSelectedRosterTeamId(option ? option.value : null);
                }}
                styles={rosterSelectStyles}
                placeholder="Select team"
                formatOptionLabel={(option) => (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {option.logo && (
                      <img
                        src={option.logo}
                        alt={option.label}
                        style={{ width: 18, height: 18 }}
                      />
                    )}
                    <span>{option.label}</span>
                  </div>
                )}
              />
            </div>
          </PanelHeader>
          <PanelBodyScroll>
            <MiniTable>
              <thead>
                <tr>
                  <th>Pos</th>
                  <th>Player</th>
                  <th>Rank</th>
                </tr>
              </thead>
              <tbody>
                {myRoster.length === 0 && (
                  <tr>
                    <td colSpan="3">No picks yet</td>
                  </tr>
                )}
                {myRoster.map((slot) => (
                  <tr key={slot.id}>
                    <td>{slot.pos}</td>
                    <td>{slot.player}</td>
                    <td>{slot.rank}</td>
                  </tr>
                ))}
              </tbody>
            </MiniTable>
          </PanelBodyScroll>

          {isDraftComplete && (
            <div style={{ padding: "8px 12px" }}>
              <button
                type="button"
                onClick={() => setIsTradeModalOpen(true)}
                style={{
                  width: "100%",
                  height: 32,
                  borderRadius: 6,
                  border: "1px solid #2cba35",
                  background: "#ffffff",
                  color: "#2cba35",
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.4px",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#2cba35";
                  e.currentTarget.style.color = "#ffffff";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#ffffff";
                  e.currentTarget.style.color = "#2cba35";
                }}
              >
                Propose Trade
              </button>
            </div>
          )}

                    <PanelHeader>Draft Needs</PanelHeader>
          <PanelBodyScroll style={{ flex: "none" }}>
            <div className="draft-needs">
              {myTeamNeeds.length === 0 && <span>No needs available</span>}
              {myTeamNeeds.map((position) => (
                <span
                  key={position}
                  style={{
                    backgroundColor: POSITIONS_COLOR[position] || "transparent",
                    color: "rgb(31, 41, 55)",
                    fontSize: 12,
                    padding: "4px 10px",
                    borderRadius: 12,
                    marginRight: 8,
                    marginBottom: 6,
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  {position}
                </span>
              ))}
            </div>
          </PanelBodyScroll>
        </Panel>

        <MainPanel>
          <DraftStatus className={showGreenStatus ? "autopick" : ""}>
            <StatusAvatar>
              {myTeamDetails?.logo ? (
                <img src={myTeamDetails.logo} alt="My team logo" />
              ) : (
                ""
              )}
            </StatusAvatar>
            <StatusText className={showGreenStatus ? "autopick" : ""}>
              {isDraftComplete ? (
                <>
                  <div className="title">Your Draft is Complete!</div>
                  <div className="subtitle">
                    Good luck in your quest to bring home this trophy
                  </div>
                </>
              ) : showAutopickStatus ? (
                <>
                  <div className="title">You&apos;re on Autopick</div>
                  <div className="subtitle">Disable Autopick to draft players manually</div>
                </>
              ) : (
                <>
                  <div className="title">{nextMyPickTitle}</div>
                  <div className="subtitle">{nextMyPickSubtitle}</div>
                </>
              )}
            </StatusText>
            {showAutopickStatus && (
              <AutopickActionButton
                type="button"
                onClick={() => setAutoPickEnabled(false)}
              >
                DISABLE AUTOPICK
              </AutopickActionButton>
            )}
          </DraftStatus>
          <>
            <TabRow>
              <Tab
                className={activeTab === "players" ? "active" : ""}
                onClick={() => setActiveTab("players")}
              >
                Players
              </Tab>
              <Tab
                className={activeTab === "history" ? "active" : ""}
                onClick={() => setActiveTab("history")}
              >
                Pick History
              </Tab>
              <ToggleRow style={{ marginLeft: "auto" }}>
                        <span>Show Drafted</span>
                        <input
                          type="checkbox"
                          checked={showDrafted}
                          onChange={(event) => setShowDrafted(event.target.checked)}
                        />
                      </ToggleRow>
            </TabRow>
            {activeTab === "players" ? (
              <>
                {isDraftComplete && (
                  <DraftCompleteMessage>
                    Your roster is now full and you've completed your draft!
                  </DraftCompleteMessage>
                )}
                {!isDraftComplete && (
                  <>
                    <FilterBar>
                      <div style={{ minWidth: 160 }}>
                        <Select
                          inputId="player-position-select"
                          aria-label="Filter by position"
                          placeholder="All Positions"
                          value={selectedPositionOptions}
                          options={positionOptions}
                          isSearchable={false}
                          isMulti
                          closeMenuOnSelect={false}
                          onChange={(options) => {
                            if (!options || options.length === 0) {
                              setSelectedPositions(["all"]);
                              return;
                            }
                            const values = options.map((option) => option.value);
                            if (values.includes("all") && values.length > 1) {
                              setSelectedPositions(values.filter((value) => value !== "all"));
                              return;
                            }
                            setSelectedPositions(values);
                          }}
                          styles={positionSelectStyles}
                          formatOptionLabel={(option, { context }) => {
                            if (context !== "menu") {
                              return option.label;
                            }
                            return (
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span
                                  aria-hidden="true"
                                  style={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    backgroundColor: option.color || "transparent",
                                    display: "inline-block",
                                  }}
                                />
                                <span>{option.label}</span>
                              </div>
                            );
                          }}
                        />
                      </div>
                      <SearchInput
                        placeholder="Player Name"
                        value={playerSearchQuery}
                        onChange={(event) => setPlayerSearchQuery(event.target.value)}
                      />
                    </FilterBar>
                    <PlayerTableWrap>
                      <PlayerTable>
                        <thead>
                          <tr>
                            <th className="cell header-cell">RK</th>
                            <th className="cell header-cell">BPA</th>
                            <th className="cell header-cell">PLAYER</th>
                            <th className="cell header-cell"></th>
                            <th className="cell header-cell">POS</th>
                            <th className="cell header-cell">SCHOOL</th>
                            <th className="cell header-cell">VALUE</th>
                          </tr>
                        </thead>
                        <tbody>
                          {isPlayersLoading && (
                            <tr>
                              <td className="cell" colSpan="19">Loading players...</td>
                            </tr>
                          )}
                          {!isPlayersLoading && filteredResults.length === 0 && (
                            <tr>
                              <td className="cell" colSpan="7">No players found.</td>
                            </tr>
                          )}
                          {!isPlayersLoading && filteredResults.map((player, idx) => {
                            const rowClassName = idx % 2 === 1 ? "even" : "odd";
                            const isPlayerDrafted = draftedPlayerIds.has(toNumber(player.id));
                            const isQueued = queuedPlayerIdSet.has(toNumber(player.id));
                            return (
                            <tr key={player.id || `${player.player}-${idx}`}>
                              <td className={`cell ${rowClassName}`}>{player.ranking || idx + 1}</td>
                              <td className={`cell ${rowClassName}`}>{player.bpa ? player.bpa : player.ranking || "-"}</td>
                              <td className={`cell ${rowClassName}`}>
                                <div className="player-name">{player.player}</div>
                              </td>
                              <td className={`cell ${rowClassName}`}>
                                {showManualDraft ? (
                                  <button
                                    className="draft-btn"
                                    disabled={isPlayerDrafted || isMakingPick}
                                    onClick={() => draftFromList(player)}
                                  >
                                    Draft
                                  </button>
                                ) : (
                                  <button
                                    className={`queue-btn${isQueued ? " queued" : ""}`}
                                    disabled={isPlayerDrafted}
                                    onClick={() => addToQueue(player)}
                                  >
                                    {isPlayerDrafted ? "Drafted" : "Queue"}
                                  </button>
                                )}
                              </td>
                              <td className={`cell ${rowClassName}`}>
                                <span
                                  className="pos-pill"
                                  style={{
                                    backgroundColor: POSITIONS_COLOR[player.position] || "transparent",
                                  }}
                                >
                                  {player.position || "-"}
                                </span>
                              </td>
                              <td className={`cell ${rowClassName}`}>{player.school || "-"}</td>
                              <td className={`cell ${rowClassName}`}>{player.value || "-"}</td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </PlayerTable>
                    </PlayerTableWrap>
                  </>
                )}
              </>
            ) : (
              <PanelBodyScroll>
                {draftedByRound.length === 0 && (
                  <div style={{ padding: "12px 16px" }}>No picks yet.</div>
                )}
                {draftedByRound.map(([roundNumber, picks]) => (
                  <div key={`round-${roundNumber}`} style={{ marginBottom: 16 }}>
                    <div style={{ padding: "8px 16px", fontWeight: 600 }}>
                      Round {roundNumber}
                    </div>
                    <PlayerTable className="pick-history-table">
                      <thead>
                        <tr>
                          <th className="cell header-cell">Pick</th>
                          <th className="cell header-cell">Team</th>
                          <th className="cell header-cell">Player</th>
                          <th className="cell header-cell">Pos</th>
                        </tr>
                      </thead>
                      <tbody>
                        {picks.map((pick, idx) => {
                          const rowClassName = idx % 2 === 1 ? "even" : "odd";
                          const teamLogo = teamById.get(pick.teamId)?.logo || "";
                          return (
                            <tr key={pick.pickId}>
                              <td className={`cell ${rowClassName}`}>{pick.pickId}</td>
                              <td className={`cell ${rowClassName}`}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  {teamLogo ? (
                                    <img
                                      src={teamLogo}
                                      alt={`${pick.teamName || "Team"} logo`}
                                      style={{ width: 20, height: 20 }}
                                    />
                                  ) : null}
                                  <span>{pick.teamName || "-"}</span>
                                </div>
                              </td>
                              <td className={`cell ${rowClassName}`}>
                                <div className="player-name">{pick.playerName || "-"}</div>
                              </td>
                              <td className={`cell ${rowClassName}`}>
                                <span
                                  className="pos-pill"
                                  style={{
                                    backgroundColor: POSITIONS_COLOR[pick.position] || "transparent",
                                  }}
                                >
                                  {pick.position || "-"}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </PlayerTable>
                  </div>
                ))}
              </PanelBodyScroll>
            )}
          </>
        </MainPanel>

        <Panel>
          <RightPanelTabRow>
            <RightPanelTab
              className={rightPanelTab === "picks" ? "active" : ""}
              onClick={() => setRightPanelTab("picks")}
            >
              Picks
            </RightPanelTab>
            <RightPanelTab
              className={rightPanelTab === "trades" ? "active" : ""}
              onClick={() => setRightPanelTab("trades")}
            >
              Trades{pendingTrades.length > 0 ? ` (${pendingTrades.length})` : ""}
            </RightPanelTab>
          </RightPanelTabRow>

          {rightPanelTab === "picks" && (
            <PicksList>
              {draftedPicks.length === 0 && (
                <PickCard key="empty-picks">
                  <div className="avatar" />
                  <div>
                    <div className="name">No picks yet</div>
                    <div className="meta">Draft results will appear here.</div>
                  </div>
                </PickCard>
              )}
              {draftedPicks.map((pickItem) => (
                <PickCard key={`${pickItem.name}-${pickItem.meta}`}>
                  <div className="avatar">
                    {pickItem.logo ? (
                      <img src={pickItem.logo} alt={`${pickItem.name} team`} />
                    ) : null}
                  </div>
                  <div>
                    <div className="name">{pickItem.name}</div>
                    <div className="meta">{pickItem.meta}</div>
                  </div>
                </PickCard>
              ))}
            </PicksList>
          )}

          {rightPanelTab === "trades" && (
            <PicksList>
              {tradesList.length === 0 && (
                <TradesEmpty>No trade offers yet</TradesEmpty>
              )}

              {pendingTrades.length > 0 && (
                <>
                  <TradeGroupLabel>Pending</TradeGroupLabel>
                  {pendingTrades.map((trade) => {
                    const isProposer = trade.proposerTeamId === myTeamId;
                    const isCounterparty = trade.counterpartyTeamId === myTeamId;
                    return (
                      <TradeOfferCard key={trade.id} className="pending">
                        <div className="trade-header">
                          <span className="trade-teams">
                            {trade.proposerTeamName} ⇄ {trade.counterpartyTeamName}
                          </span>
                          <span className="trade-status pending">Pending</span>
                        </div>
                        <div className="trade-players">
                          <div className="trade-side">
                            <div className="trade-side-label">{trade.proposerTeamName} sends</div>
                            <div className="trade-side-names">
                              {trade.proposerPlayerIds.map((id) => resolvePlayerName(id)).join(", ")}
                            </div>
                          </div>
                          <div className="trade-side">
                            <div className="trade-side-label">{trade.counterpartyTeamName} sends</div>
                            <div className="trade-side-names">
                              {trade.counterpartyPlayerIds.map((id) => resolvePlayerName(id)).join(", ")}
                            </div>
                          </div>
                        </div>
                        {trade.note && <div className="trade-note">&quot;{trade.note}&quot;</div>}
                        <div className="trade-actions">
                          {isCounterparty && (
                            <>
                              <button
                                type="button"
                                className="btn-accept"
                                onClick={() => sendTradeAction("TRADE_ACCEPT", trade.id)}
                              >
                                Accept
                              </button>
                              <button
                                type="button"
                                className="btn-reject"
                                onClick={() => sendTradeAction("TRADE_REJECT", trade.id)}
                              >
                                Reject
                              </button>
                            </>
                          )}
                          {isProposer && (
                            <button
                              type="button"
                              className="btn-cancel"
                              onClick={() => sendTradeAction("TRADE_CANCEL", trade.id)}
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      </TradeOfferCard>
                    );
                  })}
                </>
              )}

              {historyTrades.length > 0 && (
                <>
                  <TradeGroupLabel>History</TradeGroupLabel>
                  {historyTrades.map((trade) => (
                    <TradeOfferCard key={trade.id} className={trade.status}>
                      <div className="trade-header">
                        <span className="trade-teams">
                          {trade.proposerTeamName} ⇄ {trade.counterpartyTeamName}
                        </span>
                        <span className={`trade-status ${trade.status}`}>{trade.status}</span>
                      </div>
                      <div className="trade-players">
                        <div className="trade-side">
                          <div className="trade-side-label">{trade.proposerTeamName} sends</div>
                          <div className="trade-side-names">
                            {trade.proposerPlayerIds.map((id) => resolvePlayerName(id)).join(", ")}
                          </div>
                        </div>
                        <div className="trade-side">
                          <div className="trade-side-label">{trade.counterpartyTeamName} sends</div>
                          <div className="trade-side-names">
                            {trade.counterpartyPlayerIds.map((id) => resolvePlayerName(id)).join(", ")}
                          </div>
                        </div>
                      </div>
                      {trade.note && <div className="trade-note">&quot;{trade.note}&quot;</div>}
                    </TradeOfferCard>
                  ))}
                </>
              )}
            </PicksList>
          )}
        </Panel>
      </DraftRoomV2Grid>
      {isDraftComplete && isDraftCompleteModalOpen && (
        <DraftCompleteModalOverlay onClick={closeDraftCompleteModal}>
          <DraftCompleteModalCard onClick={(event) => event.stopPropagation()}>
            <Confetti numberOfPieces={800} width={700} height={700} recycle={false} />

            <DraftCompleteModalHeader>
              <h2>Your Mock Draft Result</h2>
              <DraftCompleteModalClose
                type="button"
                onClick={closeDraftCompleteModal}
                aria-label="Close mock draft result modal"
              >
                ×
              </DraftCompleteModalClose>
            </DraftCompleteModalHeader>
            <DraftCompleteModalBody>
              {modalDraftRows.length === 0 ? (
                <div className="modal-empty">No picks yet</div>
              ) : (
                <div className="modal-list">
                  {modalDraftRows.map((row) => (
                    <div className="result-row" key={row.key}>
                      <div className="round-cell">R{row.roundNumber}:P{row.pickNumber}</div>
                      <div className="player-cell">
                        <div className="team-logo">
                          {row.logo ? <img src={row.logo} alt="team logo" /> : null}
                        </div>
                        <div className="player-info">
                          <p className="name">{row.playerName}</p>
                          <p className="school">{row.school}</p>
                        </div>
                      </div>
                      <div className="pos-cell">
                        <span>{row.position}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </DraftCompleteModalBody>
            <DraftCompleteModalFooter>
              <span>Share My Top Picks!</span>
              <DraftCompleteModalCopyButton type="button" onClick={copyDraftResultLink}>
                Copy Link
              </DraftCompleteModalCopyButton>
            </DraftCompleteModalFooter>
          </DraftCompleteModalCard>
        </DraftCompleteModalOverlay>
      )}
      <ProposeTradeModal
        isOpen={isTradeModalOpen}
        onClose={() => setIsTradeModalOpen(false)}
        onPropose={sendTradePropose}
        teamList={teamList}
        slots={slots}
        drafted={drafted}
        myTeamId={myTeamDetails?.teamId ?? null}
        myTeamName={myTeamDetails?.name ?? "My Team"}
        myTeamLogo={myTeamDetails?.logo ?? ""}
      />
    </DraftRoomV2Wrapper>
  );
}

export default DraftRoomComponentV2;
